import React, { useState, useEffect } from 'react';
import { X, HardDrive, Loader2, Database, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useFileManagerStore } from '../../store/useFileManagerStore';
import { WebRTCSystem, DriveInfo } from '../../api/WebRTCSystem';
import { t } from '../../i18n';

// Format bytes to human-readable size
const formatSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  return size.toFixed(i > 1 ? 1 : 0) + ' ' + units[i];
};

interface SearchEngineInitModalProps {
  isOpen: boolean;
  onClose: () => void;
  isReinit?: boolean; // True if reinitializing (not first-time init)
}

const SearchEngineInitModal: React.FC<SearchEngineInitModalProps> = ({ isOpen, onClose, isReinit = false }) => {
  const { fs } = useFileManagerStore();
  const [drives, setDrives] = useState<DriveInfo[]>([]);
  const [selectedDrives, setSelectedDrives] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 仅 reinit 模式有效：勾选后先 clearPersist({rebuild:false}) 再 reinit，强制全盘重扫
  const [clearPersist, setClearPersist] = useState(false);

  // Load available drives when modal opens
  useEffect(() => {
    if (isOpen && fs instanceof WebRTCSystem) {
      setLoading(true);
      setError(null);
      fs.getAvailableDrivesForInit()
        .then((driveList) => {
          // Filter out V: drive (virtual drive)
          const filteredDrives = driveList.filter(d => d.volume.toUpperCase() !== 'V:');
          setDrives(filteredDrives);
          // Default: select none
          setSelectedDrives(new Set());
        })
        .catch((e) => {
          setError(e.message || t('searchEngineLoadDrivesFailed'));
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isOpen, fs]);

  const toggleDrive = (letter: string) => {
    setSelectedDrives(prev => {
      const next = new Set(prev);
      if (next.has(letter)) {
        next.delete(letter);
      } else {
        next.add(letter);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedDrives(new Set(drives.map(d => d.volume)));
  };

  const deselectAll = () => {
    setSelectedDrives(new Set());
  };

  const handleConfirm = async () => {
    if (selectedDrives.size === 0) {
      setError(t('searchEngineSelectAtLeastOne'));
      return;
    }

    if (!(fs instanceof WebRTCSystem)) {
      setError('FileSystem not ready');
      return;
    }

    setInitializing(true);
    setError(null);

    try {
      const volumes = Array.from(selectedDrives);
      if (isReinit) {
        // 勾选"清理持久化文件" → 先尝试删 .db (rebuild=false 避免双扫) 再 reinit
        // clearPersist 失败 (例如 host 还在重连 / engine 未就绪 / timeout) 不应阻塞 reinit
        // 主流程仍把 reinit 跑起来; 失败仅打 console.warn 提示
        if (clearPersist) {
          try {
            await fs.clearPersist(false);
          } catch (clearErr: any) {
            console.warn('[SearchEngineInit] clearPersist failed, continuing reinit:', clearErr);
          }
        }
        await fs.reinitSearchEngine(volumes);
      } else {
        await fs.initSearchEngine(volumes);
      }
      // Close modal on success - status will be shown in sidebar
      onClose();
    } catch (e: any) {
      setError(e.message || t('searchEngineInitFailed'));
    } finally {
      setInitializing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      data-ui-element="true"
      onClick={(e) => {
        // 阻止鼠标事件冒泡到 window（InputService 监听在 window 上），避免穿透到远程桌面
        e.nativeEvent.stopImmediatePropagation();
        if (e.target === e.currentTarget && !initializing) {
          onClose();
        }
      }}
      onMouseDown={(e) => e.nativeEvent.stopImmediatePropagation()}
      onMouseUp={(e) => e.nativeEvent.stopImmediatePropagation()}
      onMouseMove={(e) => e.nativeEvent.stopImmediatePropagation()}
      onContextMenu={(e) => e.nativeEvent.stopImmediatePropagation()}
      onWheel={(e) => e.nativeEvent.stopImmediatePropagation()}
    >
      <div className="w-full max-w-md bg-slate-900 border border-white/10 rounded-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Database size={18} className="text-win-accent" />
            <h3 className="font-medium">
              {isReinit ? t('searchEngineReinitTitle') : t('searchEngineInitTitle')}
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={initializing}
            className="p-1 hover:bg-white/10 rounded disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <p className="text-sm text-slate-400 mb-4">
            {t('searchEngineSelectDrives')}
          </p>

          {/* Loading state */}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="text-win-accent animate-spin" />
              <span className="ml-2 text-slate-400">{t('searchEngineLoadingDrives')}</span>
            </div>
          )}

          {/* Drive list */}
          {!loading && drives.length > 0 && (
            <>
              {/* Select all / Deselect all */}
              <div className="flex items-center gap-2 mb-3">
                <button
                  onClick={selectAll}
                  className="text-xs text-win-accent hover:underline"
                >
                  {t('searchEngineSelectAll')}
                </button>
                <span className="text-slate-600">|</span>
                <button
                  onClick={deselectAll}
                  className="text-xs text-slate-400 hover:underline"
                >
                  {t('searchEngineDeselectAll')}
                </button>
              </div>

              <div className="space-y-3 max-h-72 overflow-y-auto custom-scrollbar pr-1">
                {drives.map((drive) => {
                  const usedSize = drive.total_size - drive.free_size;
                  const usedPercent = drive.total_size > 0 ? (usedSize / drive.total_size) * 100 : 0;
                  const displayName = drive.label || t('searchEngineLocalDisk');

                  return (
                    <label
                      key={drive.volume}
                      className={`flex items-center gap-4 p-4 rounded-lg cursor-pointer transition-colors ${
                        selectedDrives.has(drive.volume)
                          ? 'bg-win-accent/20 border border-win-accent/50'
                          : 'bg-white/5 border border-white/10 hover:bg-white/10'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedDrives.has(drive.volume)}
                        onChange={() => toggleDrive(drive.volume)}
                        className="w-4 h-4 accent-win-accent"
                      />
                      <HardDrive size={28} className={selectedDrives.has(drive.volume) ? 'text-win-accent' : 'text-slate-400'} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">
                          {displayName} ({drive.volume})
                        </div>
                        {/* Progress bar like Windows Explorer */}
                        <div className="mt-2 h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${usedPercent > 90 ? 'bg-red-500' : 'bg-win-accent'}`}
                            style={{ width: `${Math.min(usedPercent, 100)}%` }}
                          />
                        </div>
                        <div className="text-xs text-slate-500 mt-1.5">
                          {formatSize(drive.free_size)} {t('searchEngineFree')}，共 {formatSize(drive.total_size)}
                        </div>
                      </div>
                      {selectedDrives.has(drive.volume) && (
                        <CheckCircle2 size={18} className="text-win-accent flex-shrink-0" />
                      )}
                    </label>
                  );
                })}
              </div>
            </>
          )}

          {/* No drives found */}
          {!loading && drives.length === 0 && !error && (
            <div className="flex items-center justify-center py-8 text-slate-500">
              <AlertCircle size={20} className="mr-2" />
              {t('searchEngineNoDrives')}
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mt-3 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-sm text-red-400">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-white/10 bg-black/20 flex-nowrap">
          {/* 仅 reinit 模式：清理持久化文件 checkbox。文案保持单行, 详细说明放 tooltip */}
          {isReinit ? (
            <label
              className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer select-none min-w-0 whitespace-nowrap"
              title="删除磁盘缓存，强制全盘重新扫描"
            >
              <input
                type="checkbox"
                checked={clearPersist}
                onChange={(e) => setClearPersist(e.target.checked)}
                disabled={initializing}
                className="accent-win-accent flex-shrink-0"
              />
              清理持久化文件
            </label>
          ) : <div />}

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={onClose}
              disabled={initializing}
              className="px-4 py-2 text-sm text-slate-400 hover:text-white hover:bg-white/10 rounded transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {t('cancel')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading || initializing || selectedDrives.size === 0}
              className="px-4 py-2 text-sm bg-win-accent hover:bg-win-accent/80 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
            >
              {initializing && <Loader2 size={14} className="animate-spin" />}
              {isReinit ? t('searchEngineReinitButton') : t('searchEngineInitButton')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchEngineInitModal;
